'use client';

import { TickSnapshot } from '@/types/tickSnapshot';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { TrendingUp, TrendingDown, Activity, Building2, Calendar, AlertTriangle, ChevronRight } from 'lucide-react';

interface TickSnapshotDetailModalProps {
  snapshot: TickSnapshot;
  onClose: () => void;
}

export default function TickSnapshotDetailModal({ snapshot, onClose }: TickSnapshotDetailModalProps) {
  const { tick_data, stock_info, financial_info, esm_info, gsm_info } = snapshot;
  const ohlc = tick_data?.ohlc;
  const depth = tick_data?.depth;

  // Calculate derived values
  const changePercent = ohlc?.close && ohlc.close !== 0
    ? ((snapshot.last_price - ohlc.close) / ohlc.close) * 100
    : 0;

  const buySellRatio = tick_data?.total_sell_quantity && tick_data.total_sell_quantity > 0
    ? tick_data.total_buy_quantity / tick_data.total_sell_quantity
    : null;

  const bestBidPrice = depth?.buy?.[0]?.price ?? null;
  const bestAskPrice = depth?.sell?.[0]?.price ?? null;
  const bidAskSpread = bestBidPrice && bestAskPrice
    ? ((bestAskPrice - bestBidPrice) / bestBidPrice) * 100
    : null;

  // Formatting helpers
  const formatNumber = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const sign = isNegative ? '-' : '';
    
    if (absValue >= 10000000) return `${sign}${(absValue / 10000000).toFixed(2)} Cr`;
    if (absValue >= 100000) return `${sign}${(absValue / 100000).toFixed(2)} L`;
    if (absValue >= 1000) return `${sign}${(absValue / 1000).toFixed(2)}K`;
    return `${sign}${absValue.toFixed(2)}`;
  };

  const formatVolume = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    return Math.abs(value).toLocaleString('en-IN');
  };

  const formatPercent = (value: number | null | undefined, showSign = true) => {
    if (value === null || value === undefined) return 'N/A';
    const sign = showSign && value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getValueColor = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'text-zinc-500';
    if (value > 0) return 'text-green-600 dark:text-green-400';
    if (value < 0) return 'text-red-600 dark:text-red-400';
    return 'text-zinc-500';
  };

  const getESMStageColor = (stage: number | null) => {
    if (stage === null) return 'bg-zinc-100 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-300';
    if (stage === 1) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    if (stage === 2) return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    return 'bg-zinc-100 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-300';
  };

  const getGSMStageColor = (stage: string | null) => {
    if (!stage) return 'bg-zinc-100 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-300';
    const stageNum = parseInt(stage, 10);
    if (stageNum === 0) return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    if (stageNum === 1) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    if (stageNum >= 2) return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    return 'bg-zinc-100 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-300';
  };

  const getTransactionTypeLabel = (type: string | null) => {
    if (!type) return 'N/A';
    const labels: Record<string, string> = {
      entry: 'Entry',
      exit: 'Exit',
      moved_up: 'Moved Up',
      moved_down: 'Moved Down',
    };
    return labels[type] || type;
  };

  const getTransactionTypeColor = (type: string | null) => {
    if (!type) return 'text-zinc-500';
    if (type === 'exit' || type === 'moved_down') return 'text-green-600 dark:text-green-400';
    if (type === 'entry' || type === 'moved_up') return 'text-red-600 dark:text-red-400';
    return 'text-zinc-500';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm overflow-auto p-4">
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 px-6 py-4 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div className="text-zinc-900 dark:text-white">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold">{snapshot.trading_symbol}</h2>
                <span className="text-sm px-2 py-0.5 rounded bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">{snapshot.exchange}</span>
              </div>
              {stock_info && (
                <p className="text-zinc-500 dark:text-zinc-400 text-sm mt-1">{stock_info.stock_name}</p>
              )}
              <div className="flex items-center gap-4 mt-2">
                <span className="text-3xl font-bold">₹{snapshot.last_price?.toFixed(2)}</span>
                <span className={`text-lg font-medium px-2 py-0.5 rounded ${
                  changePercent >= 0 ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' : 'bg-red-500/10 text-red-700 dark:text-red-400'
                }`}>
                  {changePercent >= 0 ? '+' : ''}{changePercent.toFixed(2)}%
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="h-6 w-6 text-zinc-500 dark:text-zinc-400" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4">
              <div className="text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Volume</div>
              <div className="text-lg font-semibold text-zinc-900 dark:text-white mt-1">{formatVolume(snapshot.volume_traded)}</div>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4">
              <div className="text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">B/S Ratio</div>
              <div className={`text-lg font-semibold mt-1 ${
                buySellRatio && buySellRatio >= 1 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}>
                {buySellRatio?.toFixed(2) ?? 'N/A'}
              </div>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4">
              <div className="text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Market Cap</div>
              <div className="text-lg font-semibold text-zinc-900 dark:text-white mt-1">{formatNumber(financial_info?.market_capitalization)} Cr</div>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4">
              <div className="text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">P/E Ratio</div>
              <div className="text-lg font-semibold text-zinc-900 dark:text-white mt-1">{financial_info?.price_to_earning?.toFixed(2) ?? 'N/A'}</div>
            </div>
          </div>

          {/* ESM & GSM Status - Prominent Section */}
          {(esm_info || gsm_info) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* ESM Info */}
              <div className={`rounded-xl p-5 border-2 ${
                esm_info?.is_in_esm 
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
                  : 'bg-zinc-50 dark:bg-zinc-800/60 border-zinc-200 dark:border-zinc-700'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className={`h-5 w-5 ${esm_info?.is_in_esm ? 'text-red-600' : 'text-zinc-400'}`} />
                    <span className="font-semibold text-zinc-900 dark:text-white">ESM Status</span>
                  </div>
                  {esm_info?.is_in_esm ? (
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getESMStageColor(esm_info.current_stage)}`}>
                      Stage {esm_info.current_stage}
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      Not in ESM
                    </span>
                  )}
                </div>
                {esm_info && (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-zinc-500 dark:text-zinc-400">Entry Date:</span>
                      <span className="ml-2 text-zinc-900 dark:text-white">{formatDate(esm_info.entry_date)}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500 dark:text-zinc-400">Last Exit:</span>
                      <span className="ml-2 text-zinc-900 dark:text-white">{formatDate(esm_info.last_exit_date)}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500 dark:text-zinc-400">State Date:</span>
                      <span className="ml-2 text-zinc-900 dark:text-white">{formatDate(esm_info.current_state_date)}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500 dark:text-zinc-400">Transaction:</span>
                      <span className={`ml-2 font-medium ${getTransactionTypeColor(esm_info.transaction_type)}`}>
                        {getTransactionTypeLabel(esm_info.transaction_type)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* GSM Info */}
              <div className={`rounded-xl p-5 border-2 ${
                gsm_info?.is_in_gsm 
                  ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800' 
                  : 'bg-zinc-50 dark:bg-zinc-800/60 border-zinc-200 dark:border-zinc-700'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className={`h-5 w-5 ${gsm_info?.is_in_gsm ? 'text-orange-600' : 'text-zinc-400'}`} />
                    <span className="font-semibold text-zinc-900 dark:text-white">GSM Status</span>
                  </div>
                  {gsm_info?.is_in_gsm ? (
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getGSMStageColor(gsm_info.current_stage)}`}>
                      Stage {gsm_info.current_stage}
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      Not in GSM
                    </span>
                  )}
                </div>
                {gsm_info && (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-zinc-500 dark:text-zinc-400">Entry Date:</span>
                      <span className="ml-2 text-zinc-900 dark:text-white">{formatDate(gsm_info.entry_date)}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500 dark:text-zinc-400">Last Exit:</span>
                      <span className="ml-2 text-zinc-900 dark:text-white">{formatDate(gsm_info.last_exit_date)}</span>
                    </div>
                    {gsm_info.criteria_name && (
                      <div className="col-span-2">
                        <span className="text-zinc-500 dark:text-zinc-400">Criteria:</span>
                        <span className="ml-2 text-zinc-900 dark:text-white">{gsm_info.criteria_name}</span>
                      </div>
                    )}
                  </div>
                )}
                {!gsm_info && (
                  <div className="text-sm text-zinc-500 dark:text-zinc-400">No GSM information available</div>
                )}
              </div>
            </div>
          )}

          {/* OHLC & Tick Data */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* OHLC */}
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-5">
              <h3 className="font-semibold text-zinc-900 dark:text-white mb-4 flex items-center gap-2">
                <Activity className="h-5 w-5 text-emerald-500" />
                OHLC Data
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex justify-between">
                  <span className="text-zinc-500 dark:text-zinc-400">Open</span>
                  <span className="font-medium text-zinc-900 dark:text-white">₹{ohlc?.open?.toFixed(2) ?? 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500 dark:text-zinc-400">High</span>
                  <span className="font-medium text-green-600 dark:text-green-400">₹{ohlc?.high?.toFixed(2) ?? 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500 dark:text-zinc-400">Low</span>
                  <span className="font-medium text-red-600 dark:text-red-400">₹{ohlc?.low?.toFixed(2) ?? 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500 dark:text-zinc-400">Close</span>
                  <span className="font-medium text-zinc-900 dark:text-white">₹{ohlc?.close?.toFixed(2) ?? 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Tick Data */}
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-5">
              <h3 className="font-semibold text-zinc-900 dark:text-white mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-500" />
                Tick Data
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500 dark:text-zinc-400">Buy Qty</span>
                  <span className="font-medium text-green-600 dark:text-green-400">{formatVolume(tick_data?.total_buy_quantity)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500 dark:text-zinc-400">Sell Qty</span>
                  <span className="font-medium text-red-600 dark:text-red-400">{formatVolume(tick_data?.total_sell_quantity)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500 dark:text-zinc-400">Avg Trade Price</span>
                  <span className="font-medium text-zinc-900 dark:text-white">₹{tick_data?.average_trade_price?.toFixed(2) ?? 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500 dark:text-zinc-400">Spread</span>
                  <span className="font-medium text-zinc-900 dark:text-white">{bidAskSpread?.toFixed(3) ?? 'N/A'}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Market Depth */}
          {depth && (
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-5">
              <h3 className="font-semibold text-zinc-900 dark:text-white mb-4">Market Depth</h3>
              <div className="grid grid-cols-2 gap-6">
                {/* Buy Side */}
                <div>
                  <div className="text-sm font-medium text-green-600 dark:text-green-400 mb-2">Buy Orders</div>
                  <div className="space-y-2">
                    {depth.buy?.slice(0, 5).map((level, idx) => (
                      <div key={idx} className="flex justify-between text-sm bg-green-50 dark:bg-green-900/20 rounded px-3 py-1.5">
                        <span className="text-zinc-700 dark:text-zinc-300">₹{level.price?.toFixed(2)}</span>
                        <span className="text-zinc-600 dark:text-zinc-400">{formatVolume(level.quantity)} ({level.orders})</span>
                      </div>
                    ))}
                    {(!depth.buy || depth.buy.length === 0) && (
                      <div className="text-sm text-zinc-500">No buy orders</div>
                    )}
                  </div>
                </div>
                {/* Sell Side */}
                <div>
                  <div className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">Sell Orders</div>
                  <div className="space-y-2">
                    {depth.sell?.slice(0, 5).map((level, idx) => (
                      <div key={idx} className="flex justify-between text-sm bg-red-50 dark:bg-red-900/20 rounded px-3 py-1.5">
                        <span className="text-zinc-700 dark:text-zinc-300">₹{level.price?.toFixed(2)}</span>
                        <span className="text-zinc-600 dark:text-zinc-400">{formatVolume(level.quantity)} ({level.orders})</span>
                      </div>
                    ))}
                    {(!depth.sell || depth.sell.length === 0) && (
                      <div className="text-sm text-zinc-500">No sell orders</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Financial Info */}
          {financial_info && (
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-5">
              <h3 className="font-semibold text-zinc-900 dark:text-white mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-emerald-500" />
                Financial Metrics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">Promoter Holding</div>
                  <div className="font-medium text-zinc-900 dark:text-white mt-1">{formatPercent(financial_info.promoter_holding, false)}</div>
                </div>
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">ROE</div>
                  <div className="font-medium text-zinc-900 dark:text-white mt-1">{financial_info.return_on_equity?.toFixed(2) ?? 'N/A'}</div>
                </div>
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">D/E Ratio</div>
                  <div className="font-medium text-zinc-900 dark:text-white mt-1">{financial_info.debt_to_equity?.toFixed(2) ?? 'N/A'}</div>
                </div>
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">EPS</div>
                  <div className="font-medium text-zinc-900 dark:text-white mt-1">₹{financial_info.eps?.toFixed(2) ?? 'N/A'}</div>
                </div>
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">P/B Ratio</div>
                  <div className="font-medium text-zinc-900 dark:text-white mt-1">{financial_info.price_to_book_value?.toFixed(2) ?? 'N/A'}</div>
                </div>
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">Down from 52W High</div>
                  <div className="font-medium text-red-600 dark:text-red-400 mt-1">{formatPercent(financial_info.down_from_52w_high, false)}</div>
                </div>
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">Avg Volume (1W)</div>
                  <div className="font-medium text-zinc-900 dark:text-white mt-1">{formatVolume(financial_info.volume_1week_average)}</div>
                </div>
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">SME</div>
                  <div className="font-medium text-zinc-900 dark:text-white mt-1">{financial_info.is_sme ? 'Yes' : 'No'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Returns */}
          {financial_info && (
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-5">
              <h3 className="font-semibold text-zinc-900 dark:text-white mb-4 flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-emerald-500" />
                Returns
              </h3>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-sm">
                <div className="text-center p-3 rounded-lg bg-white dark:bg-zinc-900">
                  <div className="text-zinc-500 dark:text-zinc-400 text-xs">1D</div>
                  <div className={`font-semibold mt-1 ${getValueColor(financial_info.return_over_1day)}`}>
                    {formatPercent(financial_info.return_over_1day)}
                  </div>
                </div>
                <div className="text-center p-3 rounded-lg bg-white dark:bg-zinc-900">
                  <div className="text-zinc-500 dark:text-zinc-400 text-xs">1W</div>
                  <div className={`font-semibold mt-1 ${getValueColor(financial_info.return_over_1week)}`}>
                    {formatPercent(financial_info.return_over_1week)}
                  </div>
                </div>
                <div className="text-center p-3 rounded-lg bg-white dark:bg-zinc-900">
                  <div className="text-zinc-500 dark:text-zinc-400 text-xs">1M</div>
                  <div className={`font-semibold mt-1 ${getValueColor(financial_info.return_over_1month)}`}>
                    {formatPercent(financial_info.return_over_1month)}
                  </div>
                </div>
                <div className="text-center p-3 rounded-lg bg-white dark:bg-zinc-900">
                  <div className="text-zinc-500 dark:text-zinc-400 text-xs">3M</div>
                  <div className={`font-semibold mt-1 ${getValueColor(financial_info.return_over_3months)}`}>
                    {formatPercent(financial_info.return_over_3months)}
                  </div>
                </div>
                <div className="text-center p-3 rounded-lg bg-white dark:bg-zinc-900">
                  <div className="text-zinc-500 dark:text-zinc-400 text-xs">6M</div>
                  <div className={`font-semibold mt-1 ${getValueColor(financial_info.return_over_6months)}`}>
                    {formatPercent(financial_info.return_over_6months)}
                  </div>
                </div>
                <div className="text-center p-3 rounded-lg bg-white dark:bg-zinc-900">
                  <div className="text-zinc-500 dark:text-zinc-400 text-xs">1Y</div>
                  <div className={`font-semibold mt-1 ${getValueColor(financial_info.return_over_1year)}`}>
                    {formatPercent(financial_info.return_over_1year)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stock Info & Links */}
          {stock_info && (
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-5">
              <h3 className="font-semibold text-zinc-900 dark:text-white mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-emerald-500" />
                Stock Information
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">ISIN</div>
                  <div className="font-medium text-zinc-900 dark:text-white mt-1">{stock_info.isin}</div>
                </div>
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">NSE Code</div>
                  <div className="font-medium text-zinc-900 dark:text-white mt-1">{stock_info.nse_code || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">BSE Code</div>
                  <div className="font-medium text-zinc-900 dark:text-white mt-1">{stock_info.bse_code || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">Stock Code</div>
                  <div className="font-medium text-zinc-900 dark:text-white mt-1">{stock_info.stock_code || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">Industry</div>
                  <div className="font-medium text-zinc-900 dark:text-white mt-1">{stock_info.industry_name || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-zinc-500 dark:text-zinc-400">Sector</div>
                  <div className="font-medium text-zinc-900 dark:text-white mt-1">{stock_info.sector_name || 'N/A'}</div>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                {stock_info.trading_view_navlink && (
                  <a
                    href={stock_info.trading_view_navlink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold transition-colors"
                  >
                    TradingView
                    <ChevronRight className="h-4 w-4" />
                  </a>
                )}
                {stock_info.screener_navlink && (
                  <a
                    href={stock_info.screener_navlink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800 rounded-lg text-sm font-semibold transition-colors"
                  >
                    Screener
                    <ChevronRight className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Timestamp */}
          <div className="text-center text-xs text-zinc-500 dark:text-zinc-400 pt-2 border-t border-zinc-200 dark:border-zinc-800">
            Captured at: {tick_data?.timestamp ? new Date(tick_data.timestamp).toLocaleString('en-IN') : 'N/A'}
          </div>
        </div>
      </div>
    </div>
  );
}

